/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { messagesConfig } from 'src/app/shared/constants/message.constants';
import { IMessage } from 'src/app/shared/models/message.modal';
import { PrecastService } from 'src/app/shared/services/precast.service';
import { MessageModalComponent } from 'src/app/shared/components/message-modal/message-modal.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SharedService } from 'src/app/features/service/shared.service';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { Router } from '@angular/router';
import { CombineTable } from '../../models/combine-indo.model';

@Component({
    selector: 'app-add-combine-table',
    templateUrl: './add-combine-table.component.html',
    styleUrls: ['./add-combine-table.component.scss'],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddCombineTableComponent implements OnInit, OnChanges {
  keepLeft = true;
  keepRight = false;
  @Input() dataSource: CombineTable[] = []; // Data from the left panel
  @Input() headerColumns: any[] = [];
  @Input() displayedColumns: any[] = [];
  @Input() soiltype = '';
  @Input() clearData = false;
  selectedProjects: CombineTable[] = []; // Store right panel selected projects' full data
  copyProjectId: string | null;
  selectedProjectId: string;
  selectedProjectIds: string[] = [];
  showBtn = false;
  parentDisplayColumns: string[] = [
    'projectNumber',
    'projectName',
    'soilType',
    'submitted',
    'sampleReceivedDate',
    'dueDate',
    'status',
  ];

  @Output() sampleCountExceeded = new EventEmitter<boolean>(); // Emit sample count exceeded status
  pageLeaveDialogRef: MatDialogRef<ConfirmationDialogComponent>;
  updateSuccess: MatDialogRef<MessageModalComponent>;
  projectNumber = '';
  projectName = '';
  selectAllChecked = false;
  isDataFetched = false;
  sampleCount = false;
  constructor(
    private apiservice: PrecastService,
    public dialog: MatDialog,
    private sharedService: SharedService,
    public router: Router,
  ) {}

  ngOnInit(): void {
    if (!this.dataSource) {
      this.dataSource = [];
    }
    this.isDataFetched = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource']) {
      // Ensure dataSource is always an array before performing operations
      if (Array.isArray(this.dataSource)) {
        this.isDataFetched = true;
        this.selectAllChecked = false;
        this.dataSource.forEach((project) => {
          if (project.isChecked === undefined) {
            project.isChecked = false; // Initialize if not already set
          }

          // Ensure the selected project is still marked as selected (based on its ID)
          if (this.selectedProjects.find((p) => p.id === project.id)) {
            project.isChecked = true;
          }
        });
      } else {
        // If dataSource is undefined or null, initialize it as an empty array
        this.dataSource = [];
        this.isDataFetched = true;
      }
    }
    if (changes['selectedProjects']) {
      this.checkSampleCount();
    }
  }

  shouldShowIndeterminateState(): boolean {
    // return this.dataSource.length > 0 && !this.selectAllChecked && this.dataSource.some((p) => p.isChecked);
    const totalProjects = this.dataSource.length;
    const checkedProjects = this.dataSource.filter((project) => project.isChecked).length;

    // Return true if some are checked but not all
    return checkedProjects > 0 && checkedProjects < totalProjects;
  }

  onSelectAllChange(): void {
    // Only add new projects to the selectedProjects list (don't reset)
    const newlySelectedProjects = this.dataSource.filter(
      (project) => !this.selectedProjects.some((p) => p.id === project.id),
    );
    if (this.selectAllChecked) {
      this.selectedProjects = [...this.selectedProjects, ...newlySelectedProjects];
    } else {
      this.selectedProjects = this.selectedProjects.filter(
        (project) => !newlySelectedProjects.some((p) => p.id === project.id),
      );
    }

    this.dataSource.forEach((project) => {
      project.isChecked = this.selectAllChecked;
    });
    this.emitCheckedProjects();
    this.checkSampleCount();
  }

  onCheckboxChange(project: CombineTable): void {
    const index = this.selectedProjects.findIndex((p) => p.id === project.id);

    if (project.isChecked && index === -1) {
      this.selectedProjects.push(project);
    } else if (!project.isChecked && index !== -1) {
      this.selectedProjects.splice(index, 1);
    }
    this.emitCheckedProjects();
    this.updateSelectAllState();
    this.checkSampleCount();
  }

  checkSampleCount(): void {
    // Calculate the total sample count across selected projects, considering both nativeSoilSamples and preCastSamples
    const totalSampleCount = this.selectedProjects.reduce(
      (count, project) => count
        + (project.nativeSoilSamples ? project.nativeSoilSamples.length : 0)
        + (project.preCastSamples ? project.preCastSamples.length : 0),
      0,
    );
    // Emit true if sample count exceeds 50, otherwise false
    const isSampleCountExceeded = totalSampleCount > 50;
    this.sampleCount = isSampleCountExceeded;
    // Emit event to notify if the sample count exceeded the limit
    this.sampleCountExceeded.emit(isSampleCountExceeded);
    // Show pop-up message if sample count exceeded the limit
    if (isSampleCountExceeded) {
      this.showPopUpMessage(messagesConfig.errorSampleMessage);
    }
  }

  emitCheckedProjects(): void {
    const selectedIds = this.selectedProjects.map((project) => project.id);
    this.selectedProjectIds = selectedIds;
  }

  updateSelectAllState(): void {
    const totalProjects = this.dataSource.length;
    const checkedProjects = this.dataSource.filter((p) => p.isChecked).length;
    this.selectAllChecked = checkedProjects === totalProjects;
    if (checkedProjects === 0) {
      this.selectAllChecked = false;
    }
  }

  getDisplayDepth(item: any): string {
    if (item.stockPile && item.displayDepth === `${null}-${null}`) {
      return 'Stockpile';
    }
    if (item.displayDepth === `${null}-${null}`) {
      return '';
    }
    return item.displayDepth;
  }

  // Method to remove a project from the selectedProjects array
  removeProject(project: CombineTable): void {
    const index = this.selectedProjects.findIndex((p) => p.id === project.id);
    if (index !== -1) {
      // Remove the project from selectedProjects
      this.selectedProjects.splice(index, 1);
      // Also deselect the checkbox on the left side (if needed)
      const leftProject = this.dataSource.find((p) => p.id === project.id);
      if (leftProject) {
        leftProject.isChecked = false;
      }
    }
    this.emitCheckedProjects();
    this.updateSelectAllState();
    this.checkSampleCount();
  }

  onProjectNumberChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.projectNumber = inputElement.value;
    this.updateCombineButtonState();
  }

  onProjectNameChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.projectName = inputElement.value;
    this.updateCombineButtonState();
  }

  onProjectSelected(event: any): void {
    const selectedProjectId = event.value;
    // First, update the selectedProjectIds array
    // Check if the project ID is already selected, and if not, add it
    if (!this.selectedProjectIds.includes(selectedProjectId)) {
      this.selectedProjectIds.push(selectedProjectId);
    }
    this.copyProjectId = selectedProjectId;
    this.updateCombineButtonState();
  }

  updateCombineButtonState() {
    const allvalue = this.selectedProjectIds.length > 1 && this.projectName && this.projectNumber && !this.sampleCount;
    if (allvalue) {
      this.showBtn = true;
    } else {
      this.showBtn = false;
    }
  }

  combine() {
    if (this.copyProjectId && this.projectName && this.projectNumber) {
      // const selectedCopyProjectId = this.copyProjectId[0];

      if (this.soiltype === 'precast') {
      // Pass the first project ID (string), not the entire array
        this.apiservice.postCombinePrecastDetail(this.selectedProjectIds, this.copyProjectId, this.projectNumber, this.projectName).subscribe({
          next: (response) => {
            if (response) {
              const successMessage: IMessage = messagesConfig.combineSubmitUserSuccessMessage;
              const url = '/dashboard';
              this.showUpdatePopUpMessage(successMessage, url);
            }
          },
          error: () => {},
        });
      } else {
        this.apiservice.postCombineNativeDetail(this.selectedProjectIds, this.copyProjectId, this.projectNumber, this.projectName).subscribe({
          next: (response) => {
            if (response) {
              const successMessage: IMessage = messagesConfig.combineSubmitUserSuccessMessage;
              const url = '/dashboard';
              this.showUpdatePopUpMessage(successMessage, url);
            }
          },
          error: () => {},
        });
      }
    }
  }

  showPopUpMessage(message: IMessage): void {
    const dialogRef = this.dialog.open(MessageModalComponent, {
      panelClass: 'app-dialog',
      data: message,
      disableClose: true,
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });

    dialogRef.afterClosed().subscribe();
  }

  onCancel() {
    const routeTo = '/dashboard';

    this.pageLeaveDialogRef = this.dialog?.open(ConfirmationDialogComponent, {
      panelClass: 'worksheet-dialog',
      data: {
        title: 'Cancel Changes',
        content: 'Are you sure you want to cancel? Your Changes will not be saved!',
        cancelText: 'NO, CONTINUE',
      },
      disableClose: true,
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });
    this.pageLeaveDialogRef?.afterClosed()?.subscribe((response?: string) => {
      if (response === 'YES') {
        this.router.navigate([routeTo]);
      }
    });
  }

  showUpdatePopUpMessage(successMessage: IMessage, routeTo: string): void {
    this.updateSuccess = this.dialog.open(MessageModalComponent, {
      panelClass: 'app-dialog',
      data: successMessage,
      disableClose: true,
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });
    this.updateSuccess.afterClosed().subscribe(() => {
      if (routeTo) {
        this.sharedService.routeValue(routeTo);
      }
    });
  }

  isSampleCountExceeded(): boolean {
    const totalSampleCount = this.selectedProjects.reduce(
      (count, project) => count
        + (project.nativeSoilSamples && project.nativeSoilSamples.length ? project.nativeSoilSamples.length : 0)
        + (project.preCastSamples && project.preCastSamples.length ? project.preCastSamples.length : 0),
      0,
    );
    return totalSampleCount > 50;
  }
}
